import React, {useEffect} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {DealDetailsButton} from 'components/Accommodation/DealDetails'
import {RoomOfferCardPriceInfo} from 'components/Accommodation/RoomOfferCardPriceInfo/RoomOfferCardPriceInfo'
import {RoomOfferCardTitle} from 'components/Accommodation/RoomOfferCardTitle'
import {useGetIsOfferSoldOut} from 'hooks/useGetIsOfferSoldOut/useGetIsOfferSoldOut'
import {useHotelRoomsFilters} from 'hooks/useHotelRoomsFilters'
import {useTrackPreferredRateOfferServed} from 'hooks/useTrackPreferredRateOfferServed'
import {
  getRoomContext,
  getRoomOfferAnalyticsContexts
} from 'middleware/analytics/selectors'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {getProactiveCheckFailedOfferIds} from 'modules/alternativeOffers/selectors'
import {OfferAccessFlow} from 'modules/sapiSearch/constants'
import {getIsAnchorHotel} from 'modules/sapiSearch/selectors'
import {
  getAccommodationPageHotel,
  getHotelApiRoomsParameters,
  getRoomOfferAccessConfig,
  getRoomOfferAppLockedConfig
} from 'modules/searchApi/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {persistAppLockedDealImpressionsCount} from '@vio/core/src/_web/appLockedDeal/impressionsCount'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {getQueryStringObject} from '@vio/core/src/_web/utils/url'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Page,
  Team
} from '@vio/core/src/analytics/types/Events'
import {
  getCancellationDeadline,
  getRefundType
} from '@vio/core/src/offer/business/cancellationPenalties'
import {getOfferPaymentMode} from '@vio/core/src/offer/business/offerPaymentMode'
import {hasOfferTag} from '@vio/core/src/offer/business/offers'
import {
  Offer,
  OfferTagLabel,
  RateChecker
} from '@vio/core/src/offer/types/offer'
import {
  getDisplayPrice,
  getTotalPrice
} from '@vio/core/src/price/business/price'
import {roomsFilterListToMap} from '@vio/core/src/room/business/utils/filters'
import {Room} from '@vio/core/src/room/types/room'
import {OfferPerks} from '@vio/shared/src/accommodation/OfferPerks/OfferPerks'
import {Cashback} from '@vio/shared/src/cashback/components/Cashback'
import {useShouldShowDealFreezeButton} from '@vio/shared/src/dealFreeze/hooks/useShouldShowDealFreezeButton'
import {EarnPoints} from '@vio/shared/src/whiteLabel/EarnPoints'
import {usePointsPriceDisplay} from '@vio/shared/src/whiteLabel/hooks/usePointsPriceDisplay'

import {RoomDetailsMountComponent} from '../RoomCard/constants'
import {RoomOfferCTA} from '../RoomOfferCTA/RoomOfferCTA'
import {RoomOfferDealFreezeBanner} from './RoomOfferDealFreezeBanner'
import {RoomOfferLayout} from './RoomOfferLayout'
import {RoomOfferSelectedLabel} from './RoomOfferSelectedLabel'
import {useRoomOfferDealFreezeBanner} from './useRoomOfferDealFreezeBanner'

const Wrapper = styled.div`
  height: 100%;
`

type Props = {
  offer: Offer
  room: Room
  component: RoomDetailsMountComponent
  //index of the offer in the context of single room
  offerIndex: number
  isVerticalLayout?: boolean
  isMountedOnOverlay?: boolean
}

export const RoomOffer = ({
  offer,
  room,
  offerIndex,
  component,
  isVerticalLayout = false,
  isMountedOnOverlay = false
}: Props) => {
  const {isMobile, isDesktop} = useDeviceLayout()
  const dispatch = useDispatch()
  const hotelId = useSelector(getAccommodationPageHotelId)
  const {ref, inView} = useInView({triggerOnce: true, threshold: 1})
  const {isClicked, id} = offer

  /* Temporarily using roomContext to determine if the offer is the cheapest */
  const roomContext = useSelector(state => getRoomContext(state, room.id))
  const isOfferCheapest = roomContext?.roomPosition === 1 && offerIndex === 0
  const {appDownloadLinkId} = useSelector(state =>
    getRoomOfferAppLockedConfig(state, id)
  )
  const isAnchorHotel = useSelector(state => getIsAnchorHotel(state, hotelId))
  const flowType = useSelector(state => getRoomOfferAccessConfig(state, id))
  const isRoomOfferAppLocked = flowType === OfferAccessFlow.AppLocked
  const isAnchorAppLockedDeal = isAnchorHotel && isRoomOfferAppLocked
  const impressionDayCount = persistAppLockedDealImpressionsCount(
    isAnchorAppLockedDeal
  )
  const hotel = useSelector(getAccommodationPageHotel)
  const {
    monetisation: {cashback}
  } = useBrandCapabilities()
  const {canEarn: hasEarnPoints} = usePointsPriceDisplay()
  const isOfferSoldOut = useGetIsOfferSoldOut({offer})
  const proactiveCheckFailedOfferIds = useSelector(
    getProactiveCheckFailedOfferIds,
    shallowEqual
  )
  const isProactiveCheckFailed = !!proactiveCheckFailedOfferIds?.[id]

  const cancellationDeadline = getCancellationDeadline(
    offer?.cancellationPenalties || []
  )

  const {checkIn, checkOut} = useSelector(state =>
    getHotelApiRoomsParameters(state, hotelId)
  )

  const refundType = getRefundType(offer?.cancellationPenalties || [])
  const paymentType = getOfferPaymentMode({offer})
  const showYourSelectionLabel = isClicked && !isOfferSoldOut
  // Used to mark the offer for automated rate verification (GHA rate checker)
  const isYourChoice = hasOfferTag(offer, OfferTagLabel.IsYourChoice)
  useTrackPreferredRateOfferServed(isYourChoice)

  const shouldShowDealFreezeButton = useShouldShowDealFreezeButton({
    offer,
    checkIn,
    checkOut,
    isOfferAppLocked: isRoomOfferAppLocked,
    hotelId,
    isOfferSoldOut
  })

  const {dealFreezeBannerVisibility} = useRoomOfferDealFreezeBanner({
    room,
    offer,
    shouldShowDealFreezeButton
  })

  const showDealFreezeBannerInDetailsSlot =
    dealFreezeBannerVisibility && isDesktop && !isVerticalLayout

  const showDealFreezeBannerInPriceSlot =
    dealFreezeBannerVisibility &&
    (isMobile || (isMountedOnOverlay && isVerticalLayout))

  // Analytics tracking
  const {activeRoomsFilters} = useHotelRoomsFilters()
  const analyticsContext = useSelector(state =>
    getRoomOfferAnalyticsContexts(state, offer.id)
  )

  useEffect(() => {
    if (inView) {
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.RoomOffer,
          action: Action.Viewed,
          team: Team.Select,
          page: Page.Accommodation,
          component,
          payload: {
            roomFilterApplied: activeRoomsFilters.length > 0,
            allSelectedRoomFilters: roomsFilterListToMap(activeRoomsFilters),
            srpSearchId: getQueryStringObject()?.srpSearchId
          },
          analyticsContext
        })
      )
      if (isRoomOfferAppLocked)
        dispatch(
          trackEvent({
            category: Category.System,
            entity: Entity.AppExclusiveOffer,
            action: Action.Displayed,
            page: Page.Accommodation,
            team: Team.Select,
            payload: {
              bannerId: appDownloadLinkId,
              impressionDayCount
            },
            analyticsContext
          })
        )
    }
  }, [dispatch, inView]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOfferSoldOut) {
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.RoomOfferSoldOut,
          action: Action.Displayed,
          page: Page.Accommodation,
          team: Team.Select,
          component,
          analyticsContext,
          payload: {
            offerCheckType: isProactiveCheckFailed ? 'preClick' : 'onClick'
          }
        })
      )
    }
  }, [
    dispatch,
    analyticsContext,
    component,
    isOfferSoldOut,
    isProactiveCheckFailed
  ])

  return (
    <Wrapper ref={ref} data-id={isYourChoice && RateChecker.GHAYourChoiceId}>
      <RoomOfferLayout
        isVerticalLayout={isVerticalLayout}
        detailsSlot={
          <>
            <div>
              <Box
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
                flexWrap="wrap"
                rowGap="s200"
                columnGap="s300"
                paddingBottom="s200"
              >
                <RoomOfferCardTitle offer={offer} />
                {showYourSelectionLabel && <RoomOfferSelectedLabel />}
              </Box>
              <OfferPerks
                paymentType={paymentType}
                isOfferSoldOut={isOfferSoldOut}
                refundType={refundType}
                cancellationDeadline={cancellationDeadline}
                mealPlan={offer.mealPlan}
                timezone={hotel?.timezone}
              />
              <Box paddingTop="s100">
                <DealDetailsButton offerId={offer.id} roomId={room.id} />
              </Box>
            </div>
            {showDealFreezeBannerInDetailsSlot && (
              <Box marginTop="s400">
                <RoomOfferDealFreezeBanner offer={offer} />
              </Box>
            )}
          </>
        }
        priceSlot={
          <>
            <RoomOfferCardPriceInfo
              hotelId={hotelId}
              offer={offer}
              isOfferCheapest={isOfferCheapest}
              isOfferSoldOut={isOfferSoldOut}
            />
            {cashback.enabled && offer.cashback && (
              <Box marginTop="s100">
                <Cashback
                  value={offer.cashback.value}
                  type={offer.cashback.type}
                />
              </Box>
            )}
            {hasEarnPoints && (
              <Box
                marginTop="s100"
                display="flex"
                justifyContent={isMobile ? 'flex-end' : 'flex-start'}
              >
                <EarnPoints
                  amount={getTotalPrice(getDisplayPrice(offer.prices ?? []))}
                />
              </Box>
            )}
            {showDealFreezeBannerInPriceSlot && (
              <Box marginBottom="s100" marginTop="s400">
                <RoomOfferDealFreezeBanner offer={offer} />
              </Box>
            )}
            <Box paddingTop="s300">
              <RoomOfferCTA
                hotelId={hotelId}
                isOfferSoldOut={isOfferSoldOut}
                offer={offer}
                shouldShowDealFreezeButton={shouldShowDealFreezeButton}
                component={component}
                room={room}
              />
            </Box>
          </>
        }
      />
    </Wrapper>
  )
}
